import { useCallback, useEffect, useMemo, useState } from 'react';
import type { LegalDocument, LegalDocumentLocale, LegalDocumentType } from '@fdh/shared';
import { adminApi } from '../../lib/adminApi';
import {
  ADMIN_HKT_DATETIME_PLACEHOLDER,
  hktWallToIso,
  isoToHktWall,
} from '../../lib/adminHktDatetime';
import { useLanguage } from '../../contexts/LanguageContext';

type LegalDocumentsPayload = { documents: LegalDocument[] };

type FormState = {
  documentType: LegalDocumentType;
  locale: LegalDocumentLocale;
  version: string;
  title: string;
  content: string;
  isActive: boolean;
  effectiveFrom: string;
};

const emptyFormState: FormState = {
  documentType: 'privacy',
  locale: 'en',
  version: 'v1',
  title: '',
  content: '',
  isActive: false,
  effectiveFrom: '',
};

const documentTypes: LegalDocumentType[] = ['privacy', 'terms', 'pics', 'subscription_terms'];
const legalLocales: LegalDocumentLocale[] = ['en', 'zh-Hant', 'zh-Hans'];

const toFormState = (item: LegalDocument): FormState => ({
  documentType: item.documentType,
  locale: item.locale,
  version: item.version,
  title: item.title,
  content: item.content,
  isActive: item.isActive,
  effectiveFrom: item.effectiveFrom ? isoToHktWall(item.effectiveFrom) : '',
});

export const AdminLegalDocumentsPage = () => {
  const { t } = useLanguage();
  const [items, setItems] = useState<LegalDocument[]>([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyFormState);

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await adminApi.get<LegalDocumentsPayload>(
        `/admin/legal-documents?includeInactive=${includeInactive ? 'true' : 'false'}`,
      );
      if (res.success && res.data?.documents) {
        setItems(res.data.documents);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.errorGeneric'));
    }
  }, [includeInactive, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const groupedItems = useMemo(
    () =>
      documentTypes.map((documentType) => ({
        documentType,
        items: items.filter((item) => item.documentType === documentType),
      })),
    [items],
  );

  const validateForm = (): boolean =>
    form.version.trim() !== '' && form.title.trim() !== '' && form.content.trim() !== '';

  const handleSubmit = async () => {
    if (!validateForm()) {
      setError(t('admin.legalFormIncomplete'));
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = {
        documentType: form.documentType,
        locale: form.locale,
        version: form.version,
        title: form.title,
        content: form.content,
        isActive: form.isActive,
        effectiveFrom:
          form.effectiveFrom.trim() === '' ? '' : hktWallToIso(form.effectiveFrom.trim()),
      };

      if (editingId) {
        await adminApi.put<LegalDocument>(`/admin/legal-documents/${editingId}`, payload);
      } else {
        await adminApi.post<LegalDocument>('/admin/legal-documents', payload);
      }
      setEditingId(null);
      setForm(emptyFormState);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.errorGeneric'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item: LegalDocument) => {
    setEditingId(item.id);
    setForm(toFormState(item));
  };

  const handleDelete = async (item: LegalDocument) => {
    setError('');
    try {
      await adminApi.delete<{ deleted: boolean }>(`/admin/legal-documents/${item.id}`);
      if (editingId === item.id) {
        setEditingId(null);
        setForm(emptyFormState);
      }
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.errorGeneric'));
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setForm(emptyFormState);
  };

  const documentTypeLabel = (documentType: LegalDocumentType): string =>
    t(`admin.legalType.${documentType}`);

  const localeLabel = (locale: LegalDocumentLocale): string => t(`admin.legalLocale.${locale}`);

  return (
    <div>
      <h1 className="text-2xl font-semibold text-white">{t('admin.nav.legalDocuments')}</h1>
      <p className="mt-1 text-sm text-slate-400">{t('admin.legalDocumentsSubtitle')}</p>

      {error ? (
        <p className="mt-4 text-sm text-red-400" role="alert">
          {error}
        </p>
      ) : null}

      <section className="mt-8 rounded-lg border border-slate-800 bg-slate-900/60 p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-medium text-white">
            {editingId ? t('admin.legalEditTitle') : t('admin.legalCreateTitle')}
          </h2>
          {editingId ? (
            <button
              type="button"
              onClick={handleCancelEdit}
              className="rounded-md border border-slate-700 px-3 py-1 text-xs text-slate-200 hover:bg-slate-800"
            >
              {t('admin.cancel')}
            </button>
          ) : null}
        </div>

        <div className="grid gap-3">
          <select
            value={form.documentType}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, documentType: e.target.value as LegalDocumentType }))
            }
            className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
          >
            {documentTypes.map((documentType) => (
              <option key={documentType} value={documentType}>
                {documentTypeLabel(documentType)}
              </option>
            ))}
          </select>

          <select
            value={form.locale}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, locale: e.target.value as LegalDocumentLocale }))
            }
            className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
          >
            {legalLocales.map((locale) => (
              <option key={locale} value={locale}>
                {localeLabel(locale)}
              </option>
            ))}
          </select>

          <input
            value={form.version}
            onChange={(e) => setForm((prev) => ({ ...prev, version: e.target.value }))}
            className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
            placeholder={t('admin.legalVersion')}
          />

          <input
            value={form.title}
            onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
            className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
            placeholder={t('admin.legalTitle')}
          />

          <textarea
            rows={18}
            value={form.content}
            onChange={(e) => setForm((prev) => ({ ...prev, content: e.target.value }))}
            className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 font-mono text-xs leading-relaxed"
            placeholder={t('admin.legalContent')}
          />
          {form.documentType === 'subscription_terms' ? (
            <p className="text-xs text-slate-400" role="note">
              {t('admin.legalSubscriptionTermsPlaceholderHint')}
            </p>
          ) : null}

          <input
            type="text"
            value={form.effectiveFrom}
            onChange={(e) => setForm((prev) => ({ ...prev, effectiveFrom: e.target.value }))}
            className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
            placeholder={ADMIN_HKT_DATETIME_PLACEHOLDER}
            aria-label={t('admin.legalEffectiveFrom')}
          />

          <label className="flex items-center gap-2 text-sm text-slate-200">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
            />
            {t('admin.legalIsActive')}
          </label>

          <button
            type="button"
            disabled={saving}
            className="rounded-md bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 disabled:opacity-60"
            onClick={() => void handleSubmit()}
          >
            {saving ? t('admin.saving') : editingId ? t('admin.update') : t('admin.create')}
          </button>
        </div>
      </section>

      <section className="mt-8">
        <label className="inline-flex items-center gap-2 text-sm text-slate-300">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          />
          {t('admin.legalShowInactive')}
        </label>

        <div className="mt-4 grid gap-4 lg:grid-cols-3">
          {groupedItems.map((group) => (
            <div key={group.documentType} className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
              <h3 className="text-sm font-semibold text-white">{documentTypeLabel(group.documentType)}</h3>
              <ul className="mt-3 space-y-2">
                {group.items.map((item) => (
                  <li key={item.id} className="rounded-md border border-slate-700 p-3 text-sm text-slate-200">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-medium text-white">{item.title}</p>
                        <p className="mt-1 text-xs text-slate-400">
                          {localeLabel(item.locale)} · {item.version} · {item.isActive ? t('admin.active') : t('admin.inactive')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleEdit(item)}
                          className="rounded border border-slate-700 px-2 py-1 text-xs hover:bg-slate-800"
                        >
                          {t('admin.edit')}
                        </button>
                        {!item.isActive ? (
                          <button
                            type="button"
                            onClick={() => void handleDelete(item)}
                            className="rounded border border-red-700/50 px-2 py-1 text-xs text-red-300 hover:bg-red-900/30"
                          >
                            {t('admin.remove')}
                          </button>
                        ) : null}
                      </div>
                    </div>
                    <p className="mt-2 line-clamp-3 text-xs text-slate-400">{item.content}</p>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};
